import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Column } from 'devextreme-react/data-grid';

import * as Actions from '../../../redux/articleActions';
import DataGrid from '../../../../../components/DataGrid';
import * as Selectors from '../../../redux/articleSelectors';
import useMainProgressContext from '../../../../../components/progress/MainProgress/context/MainProgressContext';
import useDataGridProgressContext from '../../../../../components/progress/DataGridProgress/context/DataGridProgressContext';
import { API_REQUEST_STATE } from '../../../../../core/enums/apiRequestState';
import { ROUTES } from '../../../../../core/router/routes';
import DataGridLinkTemplate from '../../../../../components/DataGrid/template/DataGridLinkTemplate';
import { DataGridCellRender } from '../../../../../components/DataGrid/model/dataGridCellRenderModel';
import { ArticleGetResponse } from '../../../model/articleModel';
import DataGridObjectTemplate from '../../../../../components/DataGrid/template/DataGridObjectTemplate';
import DataGridArrayObjectsTemplate from '../../../../../components/DataGrid/template/DataGridArrayObjectsTemplate';
import DataGridActionTemplate from '../../../../../components/DataGrid/template/DataGridActionTemplate';
import ActionConfirmationDialog from '../../../../../components/ConfirmationDialog';
import SnackbarSettings from '../../../../../components/Snackbar/SnackbarSettings';

interface ArticleDataGridProps {
  categoryId?: number;
}

const ArticleDataGrid: React.FC<ArticleDataGridProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mainProgressContext = useMainProgressContext();
  const dataGridProgressContext = useDataGridProgressContext();
  const articles = useSelector(Selectors.articles);
  const articlesState = useSelector(Selectors.apiArticlesRequestState);
  const apiRequestState = useSelector(Selectors.apiArticleRequestState);
  const apiRequestError = useSelector(Selectors.apiArticleRequestError);
  const [open, setOpen] = useState(false);
  const [articleId, setArticleId] = useState('');
  const linkRender = (cellData: DataGridCellRender<ArticleGetResponse>, name: string) => {
    return <DataGridLinkTemplate to={ROUTES.ARTICLE.EDIT} value={name} id={cellData.data.id!} />;
  };
  const handleActionClick = (id: string) => {
    setArticleId(id);
    setOpen(true);
  };

  const handleDeleteConfirm = useCallback(() => {
    dataGridProgressContext.show();
    dispatch(Actions.deleteArticle({ id: articleId }));
    setOpen(false);
  }, [articleId]);

  const handleDialogClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    dataGridProgressContext.show();
  }, []);

  useEffect(() => {
    if (props.categoryId) {
      dataGridProgressContext.show();
      dispatch(Actions.getAllArticles({ categoryId: props.categoryId }));
    }
  }, [props.categoryId]);

  useEffect(() => {
    mainProgressContext.hide();
  }, [articles]);

  useEffect(() => {
    if (articlesState === (API_REQUEST_STATE.SUCCEEDED_GET || API_REQUEST_STATE.SUCCEEDED_DELETE)) {
      setTimeout(() => {
        dataGridProgressContext.hide();
      }, 500);
    }
  }, [articlesState]);

  useEffect(
    () => () => {
      dispatch(Actions.resetArticles());
    },
    [],
  );

  return (
    <>
      <DataGrid rows={articles}>
        <Column
          dataField="id"
          caption={t('article.id')}
          width="10%"
          minWidth="100"
          cellRender={(cellData: DataGridCellRender<ArticleGetResponse>) => {
            return linkRender(cellData, cellData.data.id!);
          }}
        />
        <Column
          dataField="title"
          caption={t('article.title')}
          width="30%"
          allowSorting={false}
          minWidth="300"
          cellRender={(cellData: DataGridCellRender<ArticleGetResponse>) => {
            return linkRender(cellData, cellData.data.title);
          }}
        />
        <Column
          dataField="category"
          caption={t('article.category')}
          width="10%"
          allowSorting={false}
          minWidth="300"
          cellRender={(cellData: DataGridCellRender<ArticleGetResponse>) => {
            return <DataGridObjectTemplate value={cellData.data.category} />;
          }}
        />
        <Column
          dataField="isPublished"
          caption={t('article.is_published')}
          width="10%"
          allowSorting={false}
          minWidth="100"
          dataType="boolean"
        />
        <Column
          dataField="keywords"
          caption={t('article.keywords')}
          width="10%"
          allowSorting={false}
          minWidth="300"
          cellRender={(cellData: DataGridCellRender<ArticleGetResponse>) => {
            return <DataGridArrayObjectsTemplate value={cellData.data.keywords} />;
          }}
        />
        <Column
          dataField="tags"
          caption={t('article.tags')}
          width="10%"
          allowSorting={false}
          minWidth="300"
          cellRender={(cellData: DataGridCellRender<ArticleGetResponse>) => {
            return <DataGridArrayObjectsTemplate value={cellData.data.tags} />;
          }}
        />
        <Column
          format="dd/MM/yyyy HH:mm:ss"
          dataField="createdAt"
          dataType="date"
          caption={t('article.created_at')}
          width="10%"
          allowSorting={false}
          minWidth="200"
        />
        <Column
          format="dd/MM/yyyy HH:mm:ss"
          dataField="updatedAt"
          dataType="date"
          caption={t('article.updated_at')}
          width="10%"
          allowSorting={false}
          minWidth="200"
        />
        <Column
          dataField="actions"
          caption=""
          width="10%"
          allowSorting={false}
          minWidth="100"
          cellRender={(cellData: DataGridCellRender<ArticleGetResponse>) => {
            return <DataGridActionTemplate onClick={() => handleActionClick(cellData.data.id!)} />;
          }}
        />
      </DataGrid>
      <ActionConfirmationDialog
        open={open}
        onConfirm={handleDeleteConfirm}
        onClose={handleDialogClose}
        title={'article.confirmation.confirmation'}
        text={'article.confirmation.delete'}
      />

      <SnackbarSettings
        successMessage="article.snackbar.delete_success"
        apiRequestState={apiRequestState}
        apiRequestError={apiRequestError}
        setApiRequestState={Actions.setApiArticleRequestState}
        onSuccess={() => {
          if (props.categoryId) {
            dispatch(Actions.getAllArticles({ categoryId: props.categoryId }));
          } else {
            dispatch(Actions.getAllArticles({}));
          }
        }}
        onError={() => {
          dataGridProgressContext.hide();
        }}
      />
    </>
  );
};

export default ArticleDataGrid;
